/**
 * KnowledgeBaseSelector — Lets the user attach knowledge bases to an agent action.
 *
 * Displays available Knowledge Hubs (all source types), allows multi-select,
 * and exposes per-hub retrieval settings (topK, scoreThreshold, reasoningEffort).
 * Selected hubs are shown as chips with source-type icons.
 *
 * Writes into parameterValues['knowledgeBases'] via nodeEditorStore.setParameter.
 */

import { useCallback, useMemo, useState } from 'react';
import {
  makeStyles,
  tokens,
  Text,
  Button,
  Badge,
  Popover,
  PopoverTrigger,
  PopoverSurface,
  Checkbox,
  Field,
  SpinButton,
  Dropdown,
  Option,
  Divider,
  Tooltip,
  mergeClasses,
} from '@coreai/fluentui-react';
import {
  AddRegular,
  ArrowUploadRegular,
  DeleteRegular,
  Document20Regular,
  Globe20Regular,
  Search20Regular,
  Database20Regular,
  Settings20Regular,
  Library20Regular,
} from '@fluentui/react-icons';
import { useNodeEditorStore } from '@/stores/nodeEditorStore';
import type {
  KnowledgeBaseReference,
  KnowledgeBaseRetrievalSettings,
  KnowledgeSourceType,
} from '@/types/workflowDefinition';
import { useKnowledgeHubs, useCreateFileHub, useUploadArtifacts } from '@/hooks/useKnowledgeHubs';
import { normalizeKnowledgeHubStatus } from '@/types/knowledgeHub';
import type { KnowledgeHubStatus, KnowledgeHubType } from '@/types/knowledgeHub';
import { AddFileDialog } from '@/components/KnowledgeSources/AddFileDialog';

// =============================================================================
// TYPE MAPPING — service uses 'file'/'foundryiq'/'ai-search'/'workiq',
//                workflow definition uses 'DocumentUpload'/'FoundryIQ'/'AzureAISearch'/'WorkIQ'
// =============================================================================

const hubTypeToSourceType: Record<KnowledgeHubType, KnowledgeSourceType> = {
  file: 'DocumentUpload',
  foundryiq: 'FoundryIQ',
  'ai-search': 'AzureAISearch',
  workiq: 'WorkIQ',
};

interface AvailableKnowledgeHub {
  name: string;
  sourceType: KnowledgeSourceType;
  status: KnowledgeHubStatus;
}

// =============================================================================
// HELPERS
// =============================================================================

const sourceTypeLabels: Record<KnowledgeSourceType, string> = {
  DocumentUpload: 'Built-in',
  FoundryIQ: 'FoundryIQ',
  AzureAISearch: 'AI Search',
  WorkIQ: 'WorkIQ',
};

const sourceTypeIcons: Record<KnowledgeSourceType, React.ReactNode> = {
  DocumentUpload: <Document20Regular />,
  FoundryIQ: <Database20Regular />,
  AzureAISearch: <Search20Regular />,
  WorkIQ: <Globe20Regular />,
};

const sourceTypeBadgeColors: Record<KnowledgeSourceType, 'informative' | 'success' | 'warning' | 'brand'> = {
  DocumentUpload: 'informative',
  FoundryIQ: 'brand',
  AzureAISearch: 'success',
  WorkIQ: 'warning',
};

const reasoningEffortOptions = [
  { value: 'minimal', label: 'Minimal — no LLM' },
  { value: 'low', label: 'Low (default)' },
  { value: 'medium', label: 'Medium — full planning' },
] as const;

// =============================================================================
// STYLES
// =============================================================================

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  sectionTitle: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  description: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  kbList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  kbItem: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalS}`,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    transitionProperty: 'all',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
  },
  kbItemHover: {
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  kbItemIconContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    borderRadius: tokens.borderRadiusSmall,
    flexShrink: 0,
  },
  kbItemIcon: {
    display: 'flex',
    alignItems: 'center',
    fontSize: tokens.fontSizeBase400,
    flexShrink: 0,
  },
  kbItemContent: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '1px',
  },
  kbItemName: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  kbItemType: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  kbItemActions: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
    flexShrink: 0,
  },
  kbStatusBadge: {
    marginRight: tokens.spacingHorizontalS,
  },
  actionButton: {
    minWidth: 'auto',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalL,
    paddingInline: tokens.spacingHorizontalM,
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
  },
  emptyIcon: {
    fontSize: tokens.fontSizeHero800,
    color: tokens.colorNeutralForeground4,
  },
  // Popover hub picker
  pickerSurface: {
    maxHeight: '360px',
    width: '320px',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalM,
  },
  pickerTitle: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  pickerList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    overflow: 'auto',
    maxHeight: '260px',
  },
  pickerItem: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusSmall,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  pickerItemIcon: {
    display: 'flex',
    alignItems: 'center',
    fontSize: tokens.fontSizeBase400,
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
  },
  pickerItemContent: {
    flex: 1,
    minWidth: 0,
  },
  pickerItemName: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  pickerItemStatus: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
  },
  pickerEmpty: {
    padding: tokens.spacingVerticalM,
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  // Settings popover
  settingsSurface: {
    width: '280px',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalM,
  },
  settingsTitle: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  settingsField: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  iconBuiltIn: {
    backgroundColor: tokens.colorNeutralBackground4,
    color: tokens.colorNeutralForeground2,
  },
  iconFoundryIQ: {
    backgroundColor: tokens.colorPaletteBerryBackground2,
    color: tokens.colorNeutralForegroundOnBrand,
  },
  iconAISearch: {
    backgroundColor: tokens.colorPaletteGreenBackground2,
    color: tokens.colorNeutralForegroundOnBrand,
  },
  iconWorkIQ: {
    backgroundColor: tokens.colorPaletteMarigoldBackground2,
    color: tokens.colorNeutralForegroundOnBrand,
  },
});

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

interface RetrievalSettingsPopoverProps {
  settings: KnowledgeBaseRetrievalSettings;
  onChange: (settings: KnowledgeBaseRetrievalSettings) => void;
}

const RetrievalSettingsPopover = ({ settings, onChange }: RetrievalSettingsPopoverProps) => {
  const styles = useStyles();

  return (
    <div className={styles.settingsSurface}>
      <Text className={styles.settingsTitle}>Retrieval Settings</Text>
      <Divider />
      <Field label="Top K (results)" size="small">
        <SpinButton
          value={settings.topK ?? 5}
          min={1}
          max={50}
          onChange={(_e, data) => { if (data.value != null) onChange({ ...settings, topK: data.value }); }}
          size="small"
        />
      </Field>
      <Field label="Score Threshold" size="small">
        <SpinButton
          value={settings.scoreThreshold ?? 0.7}
          min={0}
          max={1}
          step={0.05}
          onChange={(_e, data) => { if (data.value != null) onChange({ ...settings, scoreThreshold: data.value }); }}
          size="small"
        />
      </Field>
      <Field label="Reasoning Effort" size="small">
        <Dropdown
          value={reasoningEffortOptions.find(o => o.value === (settings.retrievalReasoningEffort ?? 'low'))?.label ?? 'Low (default)'}
          selectedOptions={[settings.retrievalReasoningEffort ?? 'low']}
          onOptionSelect={(_e, data) => {
            onChange({ ...settings, retrievalReasoningEffort: data.optionValue as 'minimal' | 'low' | 'medium' });
          }}
          size="small"
        >
          {reasoningEffortOptions.map(opt => (
            <Option key={opt.value} value={opt.value}>{opt.label}</Option>
          ))}
        </Dropdown>
      </Field>
    </div>
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

const getIconStyle = (sourceType: KnowledgeSourceType, styles: ReturnType<typeof useStyles>) => {
  switch (sourceType) {
    case 'DocumentUpload': return styles.iconBuiltIn;
    case 'FoundryIQ': return styles.iconFoundryIQ;
    case 'AzureAISearch': return styles.iconAISearch;
    case 'WorkIQ': return styles.iconWorkIQ;
  }
};

export const KnowledgeBaseSelector = () => {
  const cls = useStyles();
  const parameterValues = useNodeEditorStore((s) => s.parameterValues);
  const setParameter = useNodeEditorStore((s) => s.setParameter);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [showAddFileDialog, setShowAddFileDialog] = useState(false);
  const [uploadTargetHub, setUploadTargetHub] = useState<string | null>(null);
  const [fileHubsSnapshot, setFileHubsSnapshot] = useState<string[]>([]);

  // Mutations for create hub + upload
  const createFileHubMutation = useCreateFileHub();
  const uploadArtifactsMutation = useUploadArtifacts();

  // Fetch available hubs from API
  const { data: hubsData = [] } = useKnowledgeHubs();

  // Map API hubs to the component's AvailableKnowledgeHub shape
  const allHubs: AvailableKnowledgeHub[] = useMemo(() =>
    hubsData.map(h => ({
      name: h.name,
      sourceType: hubTypeToSourceType[(h.properties.sourceType ?? h.type) as KnowledgeHubType] ?? 'DocumentUpload',
      status: normalizeKnowledgeHubStatus(h.properties.status),
    })),
    [hubsData]
  );

  // Read current knowledgeBases from parameterValues
  const knowledgeBases: KnowledgeBaseReference[] = useMemo(() => {
    const raw = parameterValues['knowledgeBases'];
    if (Array.isArray(raw)) return raw as KnowledgeBaseReference[];
    return [];
  }, [parameterValues]);

  const attachedNames = useMemo(() => new Set(knowledgeBases.map(kb => kb.knowledgeHubName)), [knowledgeBases]);

  const unattachedHubs = useMemo(() => allHubs.filter(h => !attachedNames.has(h.name)), [allHubs, attachedNames]);

  // Hubs available to add (not already attached, and ready)
  const availableHubs = useMemo(() => {
    return unattachedHubs.filter(h => h.status === 'ready');
  }, [unattachedHubs]);

  const hasUnattachedIndexingHub = useMemo(
    () => unattachedHubs.some(h => h.status === 'provisioning' || h.status === 'pending'),
    [unattachedHubs]
  );

  const hasUnattachedErrorHub = useMemo(() => unattachedHubs.some(h => h.status === 'error'), [unattachedHubs]);

  const updateKnowledgeBases = useCallback(
    (next: KnowledgeBaseReference[]) => {
      setParameter('knowledgeBases', next);
    },
    [setParameter]
  );

  const handleAttach = useCallback(
    (hub: AvailableKnowledgeHub) => {
      const entry: KnowledgeBaseReference = {
        knowledgeHubName: hub.name,
        sourceType: hub.sourceType,
        retrievalSettings: { topK: 5, scoreThreshold: 0.7, retrievalReasoningEffort: 'low' },
      };
      updateKnowledgeBases([...knowledgeBases, entry]);
    },
    [knowledgeBases, updateKnowledgeBases]
  );

  const handleDetach = useCallback(
    (name: string) => {
      updateKnowledgeBases(knowledgeBases.filter(kb => kb.knowledgeHubName !== name));
    },
    [knowledgeBases, updateKnowledgeBases]
  );

  const handleSettingsChange = useCallback(
    (name: string, settings: KnowledgeBaseRetrievalSettings) => {
      updateKnowledgeBases(
        knowledgeBases.map(kb =>
          kb.knowledgeHubName === name ? { ...kb, retrievalSettings: settings } : kb
        )
      );
    },
    [knowledgeBases, updateKnowledgeBases]
  );

  // Existing file-type hub names for the AddFileDialog
  const existingFileHubs = useMemo(
    () => hubsData.filter(h => (h.properties.sourceType ?? h.type) === 'file').map(h => h.name),
    [hubsData]
  );

  const handleCreateHubSubmit = useCallback(
    (hubName: string, files: File[], description?: string, fileDescriptions?: Record<string, string>) => {
      if (existingFileHubs.includes(hubName)) {
        uploadArtifactsMutation.mutate(
          { hubName, files, ...(fileDescriptions ? { fileDescriptions } : {}) },
          { onSettled: () => setShowAddFileDialog(false) }
        );
        return;
      }
      createFileHubMutation.mutate(
        { request: { hubName, ...(description ? { description } : {}) }, files, ...(fileDescriptions ? { fileDescriptions } : {}) },
        { onSettled: () => setShowAddFileDialog(false) }
      );
    },
    [createFileHubMutation, existingFileHubs, uploadArtifactsMutation]
  );

  const handleUploadToHubSubmit = useCallback(
    (_hubName: string, files: File[], _description?: string, fileDescriptions?: Record<string, string>) => {
      if (!uploadTargetHub) return;
      uploadArtifactsMutation.mutate(
        { hubName: uploadTargetHub, files, ...(fileDescriptions ? { fileDescriptions } : {}) },
        { onSettled: () => setUploadTargetHub(null) }
      );
    },
    [uploadArtifactsMutation, uploadTargetHub]
  );

  return (
    <div className={cls.container}>
      <Text className={cls.sectionTitle}>Knowledge</Text>
      <Text className={cls.description}>
        Attach knowledge sources so the agent can retrieve relevant context during reasoning.
      </Text>

      {/* Attached knowledge bases */}
      {knowledgeBases.length > 0 ? (
        <div className={cls.kbList}>
          {knowledgeBases.map((kb) => {
            // Find the hub for this attached knowledge base
            const hub = allHubs.find(h => h.name === kb.knowledgeHubName);
            const showIndexingBadge = hub?.status === 'provisioning' || hub?.status === 'pending';
            const showErrorBadge = hub?.status === 'error';
            return (
              <div key={kb.knowledgeHubName} className={mergeClasses(cls.kbItem, cls.kbItemHover)}>
                <div className={mergeClasses(cls.kbItemIconContainer, getIconStyle(kb.sourceType, cls))}>
                  <span className={cls.kbItemIcon}>
                    {sourceTypeIcons[kb.sourceType]}
                  </span>
                </div>
                <div className={cls.kbItemContent}>
                  <Text className={cls.kbItemName}>{kb.knowledgeHubName}</Text>
                  <Text className={cls.kbItemType}>
                    {sourceTypeLabels[kb.sourceType]}
                    {kb.retrievalSettings?.topK != null && ` · Top ${kb.retrievalSettings.topK}`}
                    {kb.retrievalSettings?.scoreThreshold != null && ` · Threshold ${kb.retrievalSettings.scoreThreshold}`}
                    {kb.retrievalSettings?.retrievalReasoningEffort && ` · ${kb.retrievalSettings.retrievalReasoningEffort.charAt(0).toUpperCase() + kb.retrievalSettings.retrievalReasoningEffort.slice(1)} effort`}
                  </Text>
                </div>
                {showIndexingBadge ? (
                  <Tooltip content="This source is still provisioning. Results may be incomplete." relationship="label">
                    <Badge className={cls.kbStatusBadge} appearance="ghost" color="informative" size="small">
                      Provisioning
                    </Badge>
                  </Tooltip>
                ) : showErrorBadge ? (
                  <Tooltip content="This hub failed indexing. Resolve the indexing error and try again." relationship="label">
                    <Badge className={cls.kbStatusBadge} appearance="ghost" color="danger" size="small">
                      Index failed
                    </Badge>
                  </Tooltip>
                ) : null}
                <div className={cls.kbItemActions}>
                  {/* Upload button (file-type hubs only) */}
                  {kb.sourceType === 'DocumentUpload' && (
                    <Tooltip content="Upload files" relationship="description">
                      <Button
                        className={cls.actionButton}
                        appearance="subtle"
                        size="small"
                        icon={<ArrowUploadRegular />}
                        aria-label={`Upload files to ${kb.knowledgeHubName}`}
                        onClick={() => {
                          setFileHubsSnapshot(existingFileHubs);
                          setUploadTargetHub(kb.knowledgeHubName);
                        }}
                      />
                    </Tooltip>
                  )}
                  {/* Settings button */}
                  <Popover withArrow>
                    <PopoverTrigger disableButtonEnhancement>
                      <Tooltip content="Retrieval settings" relationship="label">
                        <Button
                          className={cls.actionButton}
                          appearance="subtle"
                          size="small"
                          icon={<Settings20Regular />}
                          aria-label={`Settings for ${kb.knowledgeHubName}`}
                        />
                      </Tooltip>
                    </PopoverTrigger>
                    <PopoverSurface>
                      <RetrievalSettingsPopover
                        settings={kb.retrievalSettings ?? {}}
                        onChange={(s) => handleSettingsChange(kb.knowledgeHubName, s)}
                      />
                    </PopoverSurface>
                  </Popover>
                  {/* Remove button */}
                  <Tooltip content="Remove" relationship="label">
                    <Button
                      className={cls.actionButton}
                      appearance="subtle"
                      size="small"
                      icon={<DeleteRegular />}
                      aria-label={`Remove ${kb.knowledgeHubName}`}
                      onClick={() => handleDetach(kb.knowledgeHubName)}
                    />
                  </Tooltip>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className={cls.emptyState}>
          <Library20Regular className={cls.emptyIcon} />
          <Text size={200}>No knowledge sources attached</Text>
          <Text size={100}>Add knowledge so the agent can ground its responses in your data.</Text>
        </div>
      )}

      {/* Add knowledge base button / picker */}
      <Popover open={pickerOpen} onOpenChange={(_e, data) => setPickerOpen(data.open)} withArrow>
        <PopoverTrigger disableButtonEnhancement>
          <Button
            appearance="secondary"
            size="small"
            icon={<AddRegular />}
            data-testid="add-knowledge-base-button"
          >
            Add knowledge source
          </Button>
        </PopoverTrigger>
        <PopoverSurface>
          <div className={cls.pickerSurface}>
            <Text className={cls.pickerTitle}>Available Knowledge Sources</Text>
            <Divider />
            {availableHubs.length > 0 ? (
              <div className={cls.pickerList}>
                {availableHubs.map((hub) => (
                  <div key={hub.name} className={cls.pickerItem}>
                    <Checkbox
                      onChange={(_e, data) => {
                        if (data.checked) {
                          handleAttach(hub);
                        }
                      }}
                      label=""
                      aria-label={hub.name}
                    />
                    <span className={cls.pickerItemIcon}>
                      {sourceTypeIcons[hub.sourceType]}
                    </span>
                    <div className={cls.pickerItemContent}>
                      <Text className={cls.pickerItemName}>{hub.name}</Text>
                    </div>
                    <Badge
                      appearance="tint"
                      size="small"
                      color={sourceTypeBadgeColors[hub.sourceType]}
                    >
                      {sourceTypeLabels[hub.sourceType]}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <Text className={cls.pickerEmpty}>
                {allHubs.length === 0
                  ? 'No knowledge sources available. Create one below.'
                  : unattachedHubs.length === 0
                    ? 'All available knowledge sources are already attached.'
                    : hasUnattachedIndexingHub
                      ? 'Some sources are still provisioning and will appear here when ready.'
                      : hasUnattachedErrorHub
                        ? 'Some sources failed indexing and are unavailable to attach.'
                        : 'No knowledge sources are currently available to attach.'}
              </Text>
            )}
          </div>
        </PopoverSurface>
      </Popover>

      {/* Create new knowledge hub button */}
      <Button
        appearance="subtle"
        size="small"
        icon={<ArrowUploadRegular />}
        onClick={() => {
          setFileHubsSnapshot(existingFileHubs);
          setShowAddFileDialog(true);
        }}
        data-testid="create-knowledge-hub-button"
      >
        Add files
      </Button>

      {/* Create new hub dialog */}
      <AddFileDialog
        open={showAddFileDialog}
        existingHubs={showAddFileDialog ? fileHubsSnapshot : existingFileHubs}
        isSubmitting={createFileHubMutation.isPending || uploadArtifactsMutation.isPending}
        onSubmit={handleCreateHubSubmit}
        onCancel={() => setShowAddFileDialog(false)}
      />

      {/* Upload to existing hub dialog */}
      <AddFileDialog
        open={uploadTargetHub !== null}
        existingHubs={uploadTargetHub !== null ? fileHubsSnapshot : existingFileHubs}
        isSubmitting={uploadArtifactsMutation.isPending}
        {...(uploadTargetHub ? { targetHub: uploadTargetHub } : {})}
        onSubmit={handleUploadToHubSubmit}
        onCancel={() => setUploadTargetHub(null)}
      />
    </div>
  );
};

KnowledgeBaseSelector.displayName = 'KnowledgeBaseSelector';
